import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import * as _ from 'lodash';
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { ITask, ITaskFilter } from "./task.interface";
import { HttpService } from "../../../../http.service";
import { UtilsService } from "../../../shared/services/utils.service";
import { AuthService } from "../../../shared/services/auth.service";
import { apiURL, EnvConfig } from "../../../../env-config";
import { IWorker } from "../../workers-page/workers/workers.interface";

let self: TaskHttpService;

let calcPhasesTime = new Date().getTime() - 1000000;
const calcPhasesPeriod = (5 * 60 * 1000);

@Injectable()
export class TaskHttpService {
  dialogRef!: MatDialogRef<any>;

  constructor(
    private _http: HttpService,
    private _utilsService: UtilsService,
    private _dialog: MatDialog,
    private _AuthService: AuthService
  ) {
    self = this;
  }

  getTasks(filter?: ITaskFilter): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}${queryParams}`);
  }

  getTasksByNext(filter?: ITaskFilter): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/next${queryParams}`);
  }

  getBoardTasks(filter?: any): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/board${queryParams}`);
  }

  getOpenTasks(filter?: any): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/open${queryParams}`);
  }

  getTask(task: ITask | string): Observable<any> {
    let id = (typeof task == 'string') ? task : task._id;
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/${id}`);
  }

  getTasksByWorker(workers?: IWorker[] | string[] | string): Observable<any> {
    let query = {
      company: this._AuthService.companyId(),
      filters: {
        workers: !Array.isArray(workers) ? [workers] : (
          typeof workers[0] === 'string' ? workers : workers.map((el: any) => el._id)
        )
      }
    }
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/worker${queryParams}`);
  }

  getTimeTrackByTask(query: any): Observable<any> {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    return this._http.get(`${apiURL(EnvConfig.API_TIMETRACK)}/listByTask${queryParams}`);
  }

  getTimeTrackByWorker(query: any): Observable<any> {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    return this._http.get(`${apiURL(EnvConfig.API_TIMETRACK)}/listByWorker${queryParams}`);
  }

  exportTimeTrackByWorker(query: any) {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    this._http.get(`${apiURL(EnvConfig.API_TIMETRACK)}/listByWorker/export${queryParams}`, { responseType: "arraybuffer" }).subscribe(file => {
      this._utilsService.blobToFile(file, "application/xlsx", 'reporte-marcacion-x-trabajador-' + new Date().getTime() + '.xlsx');
    });
  }

  exportTimeTrackByClient(query: any) {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    this._http.get(`${apiURL(EnvConfig.API_TIMETRACK)}/listByClient/export${queryParams}`, { responseType: "arraybuffer" }).subscribe(file => {
      this._utilsService.blobToFile(file, "application/xlsx", 'reporte-marcacion-x-cliente-' + new Date().getTime() + '.xlsx');
    });
  }

  exportTimeTrackByTask(query: any) {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    this._http.get(`${apiURL(EnvConfig.API_TIMETRACK)}/listByTask/export${queryParams}`, { responseType: "arraybuffer" }).subscribe(file => {
      this._utilsService.blobToFile(file, "application/xlsx", 'reporte-marcacion-x-servicio-' + new Date().getTime() + '.xlsx');
    });
  }

  getTimeTrackByCient(query: any): Observable<any> {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    return this._http.get(`${apiURL(EnvConfig.API_TIMETRACK)}/listByClient${queryParams}`);
  }

  createTask(task: ITask): Observable<any> {
    return this._http.post(`${apiURL(EnvConfig.API_TASKS)}`, task);
  }

  updateTask(task: ITask): Observable<any> {
    let id = task._id;
    delete task._id;
    return this._http.patch(`${apiURL(EnvConfig.API_TASKS)}/${id}`, task);
  }

  deleteTask(task: ITask | string, hardDelete: boolean = false): Observable<void> {
    let id = (typeof task == 'string') ? task : task._id;
    if (hardDelete) return this._http.delete(`${apiURL(EnvConfig.API_TASKS)}/${id}`);
    return this._http.delete(`${apiURL(EnvConfig.API_TASKS)}/${id}/delete`);
  }

  restoreTask(task: ITask | string): Observable<void> {
    let id = (typeof task == 'string') ? task : task._id;
    return this._http.post(`${apiURL(EnvConfig.API_TASKS)}/${id}/restore`, null);
  }

  changeStatus(task: ITask, status: string): Observable<void> {
    let id = (typeof task == 'string') ? task : task._id;
    return this.updateTask({ _id: id, company: this._AuthService.companyId(), status: status });
  }

  totalStatus(filter?: any): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/totalStatus${queryParams}`);
  }

  statusByDate(filter?: any): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_TASKS)}/statusByDate${queryParams}`);
  }
}